/**
 * Orders API Layer
 *
 * Инкапсуляция HTTP-запросов для работы с заказами.
 * Endpoints соответствуют backend API контракту.
 */

import axios from "axios"

const API_BASE_URL =
  process.env.VUE_APP_API_BASE_URL || "https://snkrroom-resell.ru/api/resell/v1"

const api = axios.create({
  baseURL: API_BASE_URL,
})

api.interceptors.request.use(
  (config) => {
    if (typeof window !== "undefined" && window.Telegram?.WebApp?.initData) {
      config.headers["WebAppInitData"] = window.Telegram.WebApp.initData
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ============================================================================
// GET ORDERS (Reseller)
// ============================================================================

/**
 * Получить список заказов (Reseller)
 * GET /api/user/offers
 *
 * @param {Object} params - Query параметры
 * @param {string} [params.status] - pending | approved | declined
 * @param {string} [params.types] - offer | deal
 * @param {number} [params.limit]
 * @param {number} [params.offset]
 * @returns {Promise<{items: OfferWithModelResponse[], total: number}>}
 */
export async function getOrders(params = {}) {
  const response = await api.get("/user/offers", { params })
  return response.data
}

/**
 * Получить список заказов (упрощённый, возвращает массив)
 * Для обратной совместимости
 */
export async function getOrdersList() {
  const result = await getOrders()
  return result.items || result
}

/**
 * Получить один заказ из /user/offers по offer_id
 * Используется как fallback когда /seller/deal/ недоступен (для reseller)
 *
 * @param {number|string} offerId - ID оффера
 * @returns {Promise<OfferWithModelResponse|null>}
 */
export async function getOrderByOfferId(offerId) {
  const result = await getOrders()
  const items = result.items || result
  return items.find((item) => item.offer?.id === Number(offerId)) || null
}

// ============================================================================
// GET DEAL (Seller / Reseller)
// ============================================================================

/**
 * Получить детали сделки
 * GET /api/seller/deal/{deal_id}/get
 *
 * @param {number|string} dealId - ID сделки
 * @returns {Promise<DealCompositeResponse>}
 */
export async function getDeal(dealId) {
  const response = await api.get(`/seller/deal/${dealId}/get`)
  return response.data
}

/**
 * Получить детали заказа (alias для getDeal)
 */
export async function getOrder(orderId) {
  return getDeal(orderId)
}

// ============================================================================
// GET OFFER
// ============================================================================

/**
 * Получить детали оффера
 * GET /api/resell/v1/offer/{offer_id}/get
 *
 * @param {number|string} offerId - ID оффера
 * @returns {Promise<Offer>}
 */
export async function getOffer(offerId) {
  const response = await api.get(`/offer/${offerId}/get`)
  return response.data
}

// ============================================================================
// GET MODEL / PRODUCT
// ============================================================================

/**
 * Получить информацию о модели товара
 * GET /api/resell/v1/model/{model_id}/get
 *
 * @param {number|string} modelId - ID модели
 * @returns {Promise<ProductModel>}
 */
export async function getModel(modelId) {
  const response = await api.get(`/model/${modelId}/get`)
  return response.data
}

/**
 * Получить информацию о продукте
 * GET /api/resell/v1/market/product/{product_id}/get
 *
 * @param {number|string} productId - ID продукта
 * @returns {Promise<Object>}
 */
export async function getProduct(productId) {
  const response = await api.get(`/market/product/${productId}/get`)
  return response.data
}

// ============================================================================
// OFFER ACTIONS (Seller)
// ============================================================================

/**
 * Принять оффер (Seller)
 * POST /api/seller/offer/{offer_id}/accept
 *
 * @param {number|string} offerId - ID оффера
 * @returns {Promise<Object>} Обновленные данные
 */
export async function acceptOffer(offerId) {
  const response = await api.post(`/seller/offer/${offerId}/accept`)
  return response.data
}

/**
 * Отклонить оффер (Seller)
 * POST /api/seller/offer/{offer_id}/decline
 *
 * @param {number|string} offerId - ID оффера
 * @param {Object} payload - Данные отклонения
 * @param {string} payload.reason - Причина отклонения (DealCancelReason)
 * @param {string} [payload.comment] - Комментарий
 * @param {number} [payload.suggested_price] - Предложенная цена
 * @returns {Promise<Object>} Обновленные данные
 */
export async function declineOffer(offerId, payload) {
  const response = await api.post(`/seller/offer/${offerId}/decline`, payload)
  return response.data
}

// Legacy aliases
export const acceptRequest = acceptOffer
export const rejectRequest = (offerId, payload) =>
  declineOffer(offerId, payload)

/**
 * Отменить оффер (Reseller) - до оплаты
 * Примечание: контракт не определяет отдельный endpoint для отмены оффера reseller'ом
 * Используем cancel сделки если deal создан
 */
export async function cancelRequest(offerId) {
  // TODO: Уточнить endpoint для отмены оффера reseller'ом
  const response = await api.post(`/reseller/offer/${offerId}/cancel`)
  return response.data
}

// ============================================================================
// DEAL STATE UPDATE (Seller)
// ============================================================================

/**
 * Обновить состояние сделки (Seller)
 * POST /api/seller/deal/{deal_id}/state/update
 *
 * @param {number|string} dealId - ID сделки
 * @param {string} type - Тип состояния: delivery | auth | pickup | finished
 * @param {string} status - Статус: pending | success | failed
 * @returns {Promise<DealCompositeResponse>}
 */
export async function updateDealState(dealId, type, status) {
  const response = await api.post(`/seller/deal/${dealId}/state/update`, {
    type,
    status,
  })
  return response.data
}

/**
 * Подтвердить готовность к отправке (Seller)
 * Обновляет delivery state на success
 */
export async function confirmSellerReady(dealId) {
  return updateDealState(dealId, "delivery", "success")
}

/**
 * Подтвердить завершение сделки (Reseller получил товар)
 * Обновляет pickup state на success
 */
export async function confirmComplete(dealId) {
  return updateDealState(dealId, "pickup", "success")
}

// ============================================================================
// DELIVERY EXTEND (Seller / Reseller)
// ============================================================================

/**
 * Запросить продление доставки (Seller)
 * POST /api/seller/deal/{deal_id}/delivery/extend
 *
 * @param {number|string} dealId - ID сделки
 * @param {number} extraHours - Дополнительные часы
 * @returns {Promise<Object>}
 */
export async function extendDelivery(dealId, extraHours) {
  const response = await api.post(`/seller/deal/${dealId}/delivery/extend`, {
    extra_hours: extraHours,
  })
  return response.data
}

// Legacy alias
export async function extendDeadline(dealId, newDeadline) {
  // Конвертируем дату в часы
  const now = new Date()
  const deadline = new Date(newDeadline)
  const extraHours = Math.ceil((deadline - now) / (1000 * 60 * 60))
  return extendDelivery(dealId, extraHours)
}

/**
 * Принять запрос на продление (Reseller)
 * POST /api/seller/deal/{deal_id}/delivery/extend/accept
 *
 * @param {number|string} dealId - ID сделки
 * @returns {Promise<Object>}
 */
export async function acceptExtend(dealId) {
  const response = await api.post(
    `/seller/deal/${dealId}/delivery/extend/accept`
  )
  return response.data
}

/**
 * Отклонить запрос на продление (Reseller)
 * POST /api/seller/deal/{deal_id}/delivery/extend/reject
 *
 * @param {number|string} dealId - ID сделки
 * @param {string} reason - Причина: delivery_too_long | other
 * @param {string|null} comment - Комментарий
 * @returns {Promise<Object>}
 */
export async function rejectExtend(dealId, reason = "other", comment = null) {
  const response = await api.post(
    `/seller/deal/${dealId}/delivery/extend/reject`,
    {
      reason,
      comment,
    }
  )
  return response.data
}

// ============================================================================
// CANCEL DEAL
// ============================================================================

/**
 * Отменить сделку (для seller и reseller)
 * POST /api/seller/deal/{deal_id}/cancel
 *
 * Один endpoint для обоих типов пользователей.
 * Бэкенд определяет actor по авторизации.
 *
 * @param {number|string} dealId - ID сделки
 * @param {string} reason - Причина: no_stock | price_not_ok | delivery_too_long | other
 * @param {string|null} comment - Комментарий
 * @param {number|string|null} suggestedPrice - Предложенная цена (для price_not_ok)
 * @returns {Promise<DealCancelResponse>}
 */
export async function cancelDeal(
  dealId,
  reason,
  comment = null,
  suggestedPrice = null
) {
  const payload = { reason }
  if (comment) payload.comment = comment
  if (suggestedPrice) payload.suggested_price = suggestedPrice

  const response = await api.post(`/seller/deal/${dealId}/cancel`, payload)
  return response.data
}

// ============================================================================
// BIDDING (Торги)
// ============================================================================

/**
 * Создать предложение цены (bid) на сделку
 * POST /api/resell/v1/seller/deal/{deal_id}/bid
 *
 * @param {number} dealId - ID сделки
 * @param {Array<{product_id: number, seller_price: number}>} products
 * @returns {Promise<Object>}
 */
export async function bidOnDeal(dealId, products) {
  const response = await api.post(`/seller/deal/${dealId}/bid`, { products })
  return response.data
}

/**
 * Принять предложение цены (bid)
 * POST /api/resell/v1/seller/deal/{deal_id}/bid/accept
 *
 * @param {number} dealId - ID сделки
 * @returns {Promise<Object>}
 */
export async function acceptBid(dealId) {
  const response = await api.post(`/seller/deal/${dealId}/bid/accept`)
  return response.data
}

/**
 * Отклонить предложение цены (bid)
 * POST /api/resell/v1/seller/deal/{deal_id}/bid/reject
 *
 * @param {number} dealId - ID сделки
 * @returns {Promise<Object>}
 */
export async function rejectBid(dealId) {
  const response = await api.post(`/seller/deal/${dealId}/bid/reject`)
  return response.data
}

// ============================================================================
// CREATE OFFER (Reseller)
// ============================================================================

/**
 * Создать заявку (Reseller)
 * POST /api/reseller/offer/send
 *
 * @param {number} sellerId - ID продавца
 * @param {Array<{product_id: number, price: number, size: string|null}>} items
 * @returns {Promise<{id: number, status: string, created_at: string}>}
 */
export async function createOffer(sellerId, items) {
  const response = await api.post("/reseller/offer/send", {
    seller_id: sellerId,
    items,
  })
  return response.data
}

// ============================================================================
// PAYMENT (Reseller)
// ============================================================================

/**
 * Оплатить заказ (Reseller)
 * Примечание: endpoint не указан в контракте, предполагаем
 */
export async function payOrder(orderId) {
  const response = await api.post(`/reseller/order/${orderId}/pay`)
  return response.data
}

// ============================================================================
// FEEDBACK
// ============================================================================

/**
 * Отправить отзыв о сделке
 * POST /api/resell/v1/seller/deal/{deal_id}/feedback
 *
 * @param {number|string} dealId - ID сделки
 * @param {string} comment - Комментарий
 * @param {number|null} rating - Оценка (1-5)
 * @returns {Promise<void>}
 */
export async function submitFeedback(dealId, comment, rating = null) {
  const payload = { comment }
  if (rating !== null) payload.rating = rating

  await api.post(`/seller/deal/${dealId}/feedback`, payload)
}

// ============================================================================
// DEAL COUNT
// ============================================================================

/**
 * Получить количество сделок
 * GET /api/resell/v1/seller/deal/count
 *
 * @returns {Promise<{count: number}>}
 */
export async function getDealCount() {
  const response = await api.get("/seller/deal/count")
  return response.data
}

// ============================================================================
// EXPORT ALL
// ============================================================================

export default {
  getOrders,
  getOrdersList,
  getOrderByOfferId,
  getDeal,
  getOrder,
  getOffer,
  getModel,
  getProduct,
  acceptOffer,
  declineOffer,
  acceptRequest,
  rejectRequest,
  cancelRequest,
  updateDealState,
  confirmSellerReady,
  confirmComplete,
  extendDelivery,
  extendDeadline,
  acceptExtend,
  rejectExtend,
  cancelDeal,
  bidOnDeal,
  acceptBid,
  rejectBid,
  createOffer,
  payOrder,
  submitFeedback,
  getDealCount,
}
